import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import { VoiceboxCardMessage } from 'vet-bones/constants/voicebox';
import { isEqual } from 'vet-bones/utils';

import type { ProjectsAction } from 'src/actions/projects/actionCreators';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import { DEFAULT_PROJECT_PROPERTY_TYPE } from 'src/constants/project';
import { createEmptyDiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  getDefaultProjectDataPrefix,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import {
  Project,
  ProjectFileUpload,
  ProjectModel,
  ProjectsState,
  createEmptyPublishHistory,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { findMostCommonlyUsedNamespace } from 'src/utils/findMostCommonlyUsedNamespace';
import { getProjectTargetModel } from 'src/utils/getProjectTargetModel';
import { parseMetadataResults } from 'src/utils/jsonld/parseMetadataResults';
import { buildProjectModelRules } from 'src/utils/project/buildProjectModelRules';
import { getConfidenceLevel } from 'src/utils/voicebox/getConfidenceLevel';
import {
  getOutdatedQuestionIds,
  isEvaluationOutdatedForDiagram,
  isEvaluationOutdatedForQuestions,
} from 'src/utils/voicebox/isEvaluationOutdated';

export const projectsReducer = (
  prevState: ProjectsState = new ProjectsState(),
  action: ProjectsAction
): ProjectsState => {
  switch (action.type) {
    case ProjectsActionType.ADDED_PROJECT: {
      const {
        id,
        modelId,
        competencyQuestions,
        dateCreated = Date.now(),
        ...projectData
      } = action.payload;
      const diagram = createEmptyDiagramSnapshot();
      diagram.model.modelData.competencyQuestions = competencyQuestions || [];
      const project: Project = {
        id,
        ...projectData,
        dateCreated,
        dateLastUpdated: dateCreated,
        isCloudProject: false,
        isRemoved: false,
        diagram,
        fileUploadsById: {},
        virtualGraphsById: {},
        modelsById: {
          [modelId]: {
            classes: null,
            colorIdsByIri: null,
            connectionId: null,
            databaseId: null,
            databaseNamespaces: null,
            dateLastUpdated: dateCreated,
            descriptionsByIri: null,
            geoDataByIri: null,
            id: modelId,
            labelsByIri: null,
            name: projectData.name,
            namespace: getDefaultProjectModelPrefix(projectData),
            properties: null,
            rules: null,
          },
        },
        dataNamespace: '',
        propertyType: DEFAULT_PROJECT_PROPERTY_TYPE,
        publishHistory: createEmptyPublishHistory(),
        targetModelId: modelId,
      };
      project.dataNamespace = getDefaultProjectDataPrefix(project);
      const projectIds = [...prevState.projectIds, project.id];
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };
      return {
        ...prevState,
        projectIds,
        projectsById,
      };
    }

    case ProjectsActionType.ADDED_FILE_UPLOADS_TO_PROJECT: {
      const { id, fileUploads } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();
      const nextFileUploadsById = fileUploads.reduce<
        Record<string, ProjectFileUpload>
      >(
        (acc, fileUpload) => {
          acc[fileUpload.id] = {
            ...fileUpload,
            dateLastUpdated,
          };
          return acc;
        },
        {
          ...prevProject.fileUploadsById,
        }
      );

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            fileUploadsById: nextFileUploadsById,
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_FILE_UPLOAD: {
      const { id, fileUpload } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            fileUploadsById: {
              ...prevProject.fileUploadsById,
              [fileUpload.id]: {
                ...fileUpload,
                id: fileUpload.id,
                dateLastUpdated,
              },
            },
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH: {
      const { id, virtualGraph } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            virtualGraphsById: {
              ...prevProject.virtualGraphsById,
              [virtualGraph.id]: {
                ...virtualGraph,
                dateLastUpdated,
              },
            },
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.ADDED_VIRTUAL_GRAPHS_TO_PROJECT: {
      const { id, virtualGraphs } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();

      const virtualGraphsById = {
        ...prevProject.virtualGraphsById,
      };
      virtualGraphs.forEach((virtualGraph) => {
        virtualGraphsById[virtualGraph.id] = {
          ...virtualGraph,
          dateLastUpdated,
        };
      });

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            virtualGraphsById,
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.DUPLICATED_LOCAL_PROJECT: {
      const { id, copyId } = action.payload;

      const project = prevState.projectsById[id];
      if (!project) {
        return prevState;
      }

      const newProject = duplicateProject(project, {
        dateCreated: Date.now(),
        id: copyId,
        name: `${project.name} Copy`,
        isCloudProject: false,
        // do not copy voicebox related data
        voiceboxConversationId: undefined,
        voiceboxMessageHistory: undefined,
        voiceboxEvaluatedCompetencyQuestions: undefined,
        voiceboxEvaluatedSummary: undefined,
      });

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, copyId],
        projectsById: {
          ...prevState.projectsById,
          [copyId]: newProject,
        },
      };
    }

    case ProjectsActionType.LOADED_PROJECT_FROM_CLOUD: {
      const { project } = action.payload;

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, project.id],
        projectsById: {
          ...prevState.projectsById,
          [project.id]: project,
        },
      };
    }

    case ProjectsActionType.IMPORTED_PROJECT_FROM_FILE: {
      const { project } = action.payload;

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, project.id],
        projectsById: {
          ...prevState.projectsById,
          [project.id]: project,
        },
      };
    }

    case ProjectsActionType.ARCHIVED_PROJECT: {
      // these have the potential to be restored
      // Just add the isRemoved flag; projectIds and projectsById
      // are updated later in /src/store/prune/prune.ts
      // If the project is cloud, it will not be tagged because it will be
      // removed from the cloud storage
      const { id } = action.payload;
      const isCloudProject = Boolean(
        prevState.projectsById[id]?.isCloudProject
      );
      const project = {
        ...prevState.projectsById[id],
        isRemoved: !isCloudProject,
      };
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };

      return {
        ...prevState,
        projectsById,
      };
    }

    case ProjectsActionType.MIGRATED_LOCAL_PROJECT_TO_CLOUD: {
      const { id } = action.payload;
      const project = prevState.projectsById[id];
      if (!project) {
        return prevState;
      }

      return {
        ...prevState,
        projectIds: prevState.projectIds.filter(
          (projectId) => projectId !== id
        ),
        projectsById: omit(prevState.projectsById, id),
      };
    }

    case ProjectsActionType.MIGRATED_LOCAL_PROJECT_BACKUP_TO_CLOUD: {
      const { id } = action.payload;
      const project = prevState.projectsById[id];
      if (!project) {
        return prevState;
      }

      return {
        ...prevState,
        projectIds: prevState.projectIds.filter(
          (projectId) => projectId !== id
        ),
        projectsById: omit(prevState.projectsById, id),
      };
    }

    case ProjectsActionType.MIGRATED_CLOUD_PROJECT_TO_LOCAL: {
      const { id, copyId } = action.payload;

      const project = prevState.projectsById[id];
      if (!project) {
        return prevState;
      }

      const newProject = duplicateProject(project, {
        dateCreated: Date.now(),
        id: copyId,
        name: project.name,
        isCloudProject: false,
      });

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, copyId],
        projectsById: {
          ...prevState.projectsById,
          [copyId]: newProject,
        },
      };
    }

    case ProjectsActionType.DUPLICATED_CLOUD_BACKUP_TO_LOCAL_PROJECT: {
      const { id } = action.payload;
      const project = prevState.projectsById[id];
      if (!project) {
        return prevState;
      }

      const newProject = {
        ...project,
        dateLastUpdated: Date.now(),
        cloudProjectId: undefined,
        cloudProjectUpdatedAt: undefined,
        isCloudProject: false,
      };

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: newProject,
        },
      };
    }

    case ProjectsActionType.BACKED_UP_CLOUD_PROJECT_TO_LOCAL_PROJECT: {
      const { project } = action.payload;

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, project.id],
        projectsById: {
          ...prevState.projectsById,
          [project.id]: project,
        },
      };
    }

    case ProjectsActionType.RENAMED_PROJECT: {
      const { id, name } = action.payload;
      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const project: Project = {
        ...prevProject,
        name,
      };

      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };

      return {
        ...prevState,
        projectsById,
      };
    }

    case ProjectsActionType.RESTORED_LOCAL_PROJECT: {
      const { id } = action.payload;
      const project = {
        ...prevState.projectsById[id],
        isRemoved: false,
      };
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };

      return {
        ...prevState,
        projectsById,
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_CONNECTION_ID: {
      const { id, connectionId } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            connectionId,
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_DIAGRAM: {
      const { id, diagram } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      let voiceboxEvaluatedCompetencyQuestions: string[] | undefined;
      const isDiagramOutdated = isEvaluationOutdatedForDiagram(
        prevProject.diagram,
        diagram
      );

      if (
        prevProject.voiceboxEvaluatedCompetencyQuestions &&
        !isDiagramOutdated
      ) {
        const outdatedQuestionIds = getOutdatedQuestionIds(
          prevProject.diagram,
          diagram
        );
        voiceboxEvaluatedCompetencyQuestions = outdatedQuestionIds.size
          ? prevProject.voiceboxEvaluatedCompetencyQuestions.filter(
              (questionId) => !outdatedQuestionIds.has(questionId)
            )
          : prevProject.voiceboxEvaluatedCompetencyQuestions;
      }

      let voiceboxEvaluatedSummary: boolean | undefined;
      if (
        prevProject.voiceboxEvaluatedSummary &&
        !isDiagramOutdated &&
        !isEvaluationOutdatedForQuestions(prevProject.diagram, diagram)
      ) {
        voiceboxEvaluatedSummary = prevProject.voiceboxEvaluatedSummary;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            diagram,
            voiceboxEvaluatedCompetencyQuestions,
            voiceboxEvaluatedSummary,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_DESCRIPTION: {
      const { id, description } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            description,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_MODEL_NAMESPACE: {
      const { id, modelId, namespace } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }
      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                namespace,
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_DATA_NAMESPACE: {
      const { id, dataNamespace } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            dataNamespace,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_PROPERTY_TYPE: {
      const { id, propertyType } = action.payload;
      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated: Date.now(),
            propertyType,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_MODEL_NAME: {
      const { id, modelId, modelName } = action.payload;
      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                name: modelName,
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_TARGET_MODEL_ID: {
      const { id, modelId } = action.payload;
      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const nextModelId = modelId;
      if (prevProject.targetModelId === nextModelId) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            targetModelId: nextModelId,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_VOICEBOX_CONVERSATION: {
      const { id, conversationId } = action.payload;
      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      if (prevProject.voiceboxConversationId === conversationId) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            voiceboxConversationId: conversationId,
          },
        },
      };
    }

    case ProjectsActionType.CLEARED_PROJECT_VOICEBOX_EVALUATION: {
      const { id, clearSummary } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            voiceboxEvaluatedCompetencyQuestions: undefined,
            voiceboxEvaluatedSummary: undefined,
            voiceboxEvaluationSummary: clearSummary
              ? undefined
              : prevProject.voiceboxEvaluationSummary,
          },
        },
      };
    }

    case ProjectsActionType.EVALUATED_PROJECT_COMPETENCY_QUESTIONS: {
      const { id, questionIds } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            voiceboxEvaluatedCompetencyQuestions: uniq([
              ...(prevProject.voiceboxEvaluatedCompetencyQuestions || []),
              ...questionIds,
            ]),
          },
        },
      };
    }

    case ProjectsActionType.EVALUATED_PROJECT_SUMMARY: {
      const { id, evaluationSummary } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            voiceboxEvaluatedSummary: true,
            voiceboxEvaluationSummary: evaluationSummary,
          },
        },
      };
    }

    case ProjectsActionType.OPENED_PROJECT_COMPETENCY_QUESTION: {
      const { id, question } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevMessage =
        prevProject.voiceboxMessageHistory &&
        prevProject.voiceboxMessageHistory[
          prevProject.voiceboxMessageHistory.length - 1
        ];

      const nextMessage: VoiceboxCardMessage = {
        messageId: `question-${question.id}-${dateLastUpdated}`,
        request: {
          confidenceLevel: getConfidenceLevel(question.evaluation?.score),
          content: question.question,
        },
        response: {
          content: question.evaluation?.explanation,
          query: question.evaluation?.query || undefined,
          title: copy.components.project.competencyQuestions.cardTitle,
        },
        isCard: true,
      };

      if (
        isEqual(omit(prevMessage, 'messageId'), omit(nextMessage, 'messageId'))
      ) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            voiceboxMessageHistory: (
              prevProject.voiceboxMessageHistory || []
            ).concat([nextMessage]),
          },
        },
      };
    }

    case ProjectsActionType.ADDED_LOCAL_MODEL_TO_PROJECT: {
      const { id, partialModel } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();
      const model: ProjectModel = {
        connectionId: null,
        databaseId: null,
        databaseNamespaces: null,
        dateLastUpdated,
        classes: null,
        properties: null,
        colorIdsByIri: null,
        descriptionsByIri: null,
        geoDataByIri: null,
        rules: null,
        labelsByIri: null,
        ...partialModel,
      };

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            modelsById: {
              ...prevProject.modelsById,
              [model.id]: model,
            },
          },
        },
      };
    }

    case ProjectsActionType.ADD_IMPORTED_MODELS_TO_PROJECT_STARTED: {
      const {
        connectionId,
        databaseId,
        databaseNamespaces,
        id,
        modelNamesAndIds,
      } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();
      const modelsById = modelNamesAndIds.reduce<Record<string, ProjectModel>>(
        (acc, { modelName, modelId }) => {
          acc[modelId] = {
            classes: null,
            colorIdsByIri: null,
            connectionId,
            databaseId,
            databaseNamespaces,
            dateLastUpdated,
            descriptionsByIri: null,
            geoDataByIri: null,
            id: modelId,
            importedModelName: modelName,
            labelsByIri: null,
            name: modelName,
            namespace: getProjectTargetModel(prevProject).namespace,
            properties: null,
            rules: null,
          };
          return acc;
        },
        { ...prevProject.modelsById }
      );

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            modelsById,
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const results = action.payload.response?.body || [];

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      const { colorIdsByIri, descriptionsByIri, geoDataByIri, labelsByIri } =
        parseMetadataResults(results);

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                colorIdsByIri: {
                  ...prevModel.colorIdsByIri,
                  ...colorIdsByIri,
                },
                descriptionsByIri: {
                  ...prevModel.descriptionsByIri,
                  ...descriptionsByIri,
                },
                geoDataByIri: {
                  ...prevModel.geoDataByIri,
                  ...geoDataByIri,
                },
                labelsByIri: {
                  ...prevModel.labelsByIri,
                  ...labelsByIri,
                },
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_GEO_DATA_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const results = action.payload.response?.body || [];

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      const { geoDataByIri } = parseMetadataResults(results);

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                geoDataByIri: {
                  ...prevModel.geoDataByIri,
                  ...geoDataByIri,
                },
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const bindings = action.payload.response?.body?.results?.bindings || [];

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      const rules = buildProjectModelRules({
        bindings,
        classes: prevModel.classes,
        colorIdsByIri: prevModel.colorIdsByIri,
        databaseNamespaces: prevModel.databaseNamespaces || {},
        labelsByIri: prevModel.labelsByIri,
        modelName: prevModel.name,
        properties: prevModel.properties,
      });

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                rules,
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.ADD_IMPORTED_MODELS_TO_PROJECT_FINISHED: {
      const { id, modelNamesAndIds } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const modelsById = modelNamesAndIds.reduce(
        (acc, { modelId }) => {
          const prevModel = acc[modelId];
          if (prevModel) {
            const mostUsedNamespace = findMostCommonlyUsedNamespace(prevModel);
            if (mostUsedNamespace) {
              acc[modelId] = {
                ...prevModel,
                namespace: mostUsedNamespace,
              };
            }
          }

          return acc;
        },
        { ...prevProject.modelsById }
      );

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById,
          },
        },
      };
    }

    case ProjectsActionType.ADDED_SUGGESTIONS_TO_PROJECT: {
      const { id } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            voiceboxMessageHistory: (
              prevProject.voiceboxMessageHistory || []
            ).concat([
              {
                conversationId: '',
                messageId: '',
                result: copy.components.voicebox.history.suggestions,
                success: true,
                isResponse: true,
              },
            ]),
          },
        },
      };
    }

    case ProjectsActionType.ACCEPTED_PROJECT_DRAFT_DIAGRAM: {
      const { id, diagram, messages } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...omit(prevProject, 'voiceboxConversationId'),
            dateLastUpdated,
            diagram,
            modelsById: Object.fromEntries(
              Object.entries(prevProject.modelsById).map(([modelId, model]) => [
                modelId,
                omit(model, 'draft'),
              ])
            ),
            voiceboxMessageHistory: (
              prevProject.voiceboxMessageHistory || []
            ).concat(messages, [
              {
                messageId: '',
                text: copy.components.voicebox.history.accepted,
                isDivider: true,
              },
            ]),
          },
        },
      };
    }

    case ProjectsActionType.REJECTED_PROJECT_DRAFT_DIAGRAM: {
      const { id, diagram, messages, fileUploadIdsToRemove } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const nextFileUploadsById =
        fileUploadIdsToRemove && fileUploadIdsToRemove.length > 0
          ? Object.fromEntries(
              Object.entries(prevProject.fileUploadsById).filter(
                ([fileUploadId]) =>
                  !fileUploadIdsToRemove.includes(fileUploadId)
              )
            )
          : prevProject.fileUploadsById;

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...omit(prevProject, 'voiceboxConversationId'),
            dateLastUpdated,
            diagram,
            fileUploadsById: nextFileUploadsById,
            modelsById: Object.fromEntries(
              Object.entries(prevProject.modelsById)
                .filter(([, model]) => !model.draft)
                .map(([modelId, model]) => [modelId, omit(model, 'draft')])
            ),
            voiceboxMessageHistory: (
              prevProject.voiceboxMessageHistory || []
            ).concat(messages, [
              {
                messageId: '',
                text: copy.components.voicebox.history.rejected,
                isDivider: true,
              },
            ]),
          },
        },
      };
    }

    case ProjectsActionType.PUBLISH_PROJECT_FINISHED: {
      const { id, publishHistory } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            publishHistory,
          },
        },
      };
    }

    case ProjectsActionType.ADDED_PENDING_MODEL_DATA_TO_PROJECT: {
      const { id, modelId, classes, properties } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                classes,
                properties,
              },
            },
          },
        },
      };
    }

    default:
      return prevState;
  }
};
